/**
 * <s-auto-scroll> slowly drifts a horizontal scroll track ([data-auto-scroll-track]) in a seamless loop
 * on small screens. Visitors can still swipe; drifting pauses while touching and resumes shortly after.
 * Off on wider screens (data-max-width, default 767px) and when reduced motion is preferred.
 * Loop copies of the items are aria-hidden and inert, so assistive tech and keyboards see each item once.
 */
class SAutoScroll extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-auto-scroll-track]');
    if (!this.track) return;
    this.speed = Number(this.dataset.speed) || 22; // pixels per second
    const maxWidth = this.dataset.maxWidth || '767px';
    this.small = window.matchMedia(`(max-width: ${maxWidth})`);
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.paused = false;
    this.position = 0;
    this.onChange = () => this.update();
    this.onPause = () => this.pause();
    this.onResume = () => this.resume();
    this.small.addEventListener('change', this.onChange);
    this.reduced.addEventListener('change', this.onChange);
    this.track.addEventListener('pointerdown', this.onPause);
    this.track.addEventListener('touchstart', this.onPause, { passive: true });
    this.track.addEventListener('pointerup', this.onResume);
    this.track.addEventListener('touchend', this.onResume, { passive: true });
    this.track.addEventListener('focusin', this.onPause);
    this.track.addEventListener('focusout', this.onResume);
    this.update();
  }

  disconnectedCallback() {
    this.stop();
    this.small?.removeEventListener('change', this.onChange);
    this.reduced?.removeEventListener('change', this.onChange);
  }

  update() {
    if (this.small.matches && !this.reduced.matches) {
      this.addClones();
      this.start();
    } else {
      this.stop();
      this.removeClones();
    }
  }

  addClones() {
    if (this.track.querySelector('[data-auto-scroll-clone]')) return;
    const originals = Array.from(this.track.children);
    originals.forEach((item) => {
      const clone = item.cloneNode(true);
      clone.setAttribute('data-auto-scroll-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.inert = true;
      clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      clone.querySelectorAll('[data-shopify-editor-block]').forEach((el) => el.removeAttribute('data-shopify-editor-block'));
      this.track.appendChild(clone);
    });
    this.firstClone = this.track.querySelector('[data-auto-scroll-clone]');
    this.dataset.active = '';
  }

  removeClones() {
    this.track.querySelectorAll('[data-auto-scroll-clone]').forEach((el) => el.remove());
    this.firstClone = null;
    delete this.dataset.active;
  }

  start() {
    if (this.frame) return;
    this.position = this.track.scrollLeft;
    this.last = performance.now();
    const step = (now) => {
      const delta = Math.min(now - this.last, 100) / 1000;
      this.last = now;
      if (!this.paused && this.firstClone) {
        const loopWidth = this.firstClone.offsetLeft - this.track.firstElementChild.offsetLeft;
        this.position += this.speed * delta;
        if (loopWidth > 0 && this.position >= loopWidth) this.position -= loopWidth;
        this.track.scrollLeft = this.position;
      }
      this.frame = requestAnimationFrame(step);
    };
    this.frame = requestAnimationFrame(step);
  }

  stop() {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = null;
  }

  pause() {
    this.paused = true;
    clearTimeout(this.resumeTimer);
  }

  resume() {
    clearTimeout(this.resumeTimer);
    this.resumeTimer = setTimeout(() => {
      if (this.firstClone) {
        const loopWidth = this.firstClone.offsetLeft - this.track.firstElementChild.offsetLeft;
        if (loopWidth > 0 && this.track.scrollLeft >= loopWidth) this.track.scrollLeft -= loopWidth;
      }
      this.position = this.track.scrollLeft;
      this.paused = false;
    }, 2500);
  }
}

if (!customElements.get('s-auto-scroll')) customElements.define('s-auto-scroll', SAutoScroll);
